import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FileInfo } from "src/app/common/components/file-content-list/file-content-list.component";
import { DragAndDropItem } from "src/app/common/models/drag-and-drop-item.model";
import { ConfigService } from "src/app/common/services/config.service";
import { FloatingAssistantChatService } from "src/app/common/services/floating-assistant-chat.service";
import { LlmConfigService } from "src/app/common/services/llm-config.service";
@Component({
  templateUrl: "./chat-input.component.html",
  selector: "app-chat-input",
  styleUrls: ["./chat-input.component.scss"],
})
export class ChatInputComponent implements OnChanges {
  @Input() textAreaInput = "";
  @Input() disabled = false;
  @Input() genPending = false;
  @Input() canAddFiles = false;
  @Input() showSearchToggle = false; // search toggle
  @Input() showImageCreationToggle = false; // image creation/edition toggle
  @Input() forceEnableSearch = false;
  @Input() modelTechnicalName = "";

  searchEnabled = false;
  imageCreationEnabled = false;
  private lastModelBeforeImageCreation: string | null = null;

  // Dynamic tooltip: includes the default search model name
  private readonly configService: ConfigService = inject(ConfigService);

  @Output() readonly sendClick = new EventEmitter<{
    prompt: string;
    files?: FileInfo[];
    content?: DragAndDropItem;
    enableSearch: boolean;
    enableImageCreation: boolean;
  }>();
  @Output() readonly stopClick = new EventEmitter<string>();
  @Output() readonly textAreaInputChanged = new EventEmitter<string>();

  inputFiles: FileInfo[] = [];
  inputContent?: DragAndDropItem = undefined;

  protected readonly floatingAssistantChatService = inject(FloatingAssistantChatService);
  protected readonly llmConfigService = inject(LlmConfigService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["modelTechnicalName"]) {
      // If the newly‐selected model does NOT support search, force OFF:
      if (!this.isSearchSupported && this.searchEnabled) {
        this.searchEnabled = false;
      }
      // If the newly‐selected model does NOT support image creation, force off:
      if (!this.isImageCreationSupported && this.imageCreationEnabled) {
        this.imageCreationEnabled = false;
      }
    }

    // If parent tells to “force” enable search, do so (but only if model actually supports it)
    if (changes["forceEnableSearch"]) {
      if (this.forceEnableSearch && this.isSearchSupported) {
        this.searchEnabled = true;
      }
      // If parent says "forceEnableSearch = false", turn it OFF unconditionally
      else if (!this.forceEnableSearch) {
        this.searchEnabled = false;
      }
    }
  }

  onTextAreaInputChanged(event: Event): void {
    this.textAreaInputChanged.emit((event.target as HTMLTextAreaElement).value);
  }

  onEnterControlKey(): void {
    if (this.genPending) {
      return;
    }
    this.sendOrStop();
  }

  toggleSearch(): void {
    if (this.disabled) {
      return;
    }
    // Switching off image creation if enabled
    if (this.imageCreationEnabled) {
      this.toggleImageCreation();
    }
    this.searchEnabled = !this.searchEnabled;
  }

  toggleImageCreation(): void {
    if (this.disabled) {
      return;
    }
    if (!this.imageCreationEnabled) {
      // Turning ON: save current model so can be restored
      this.lastModelBeforeImageCreation = this.modelTechnicalName;

      // Switching off search if search is enabled
      if (this.searchEnabled) {
        this.searchEnabled = false;
      }
    }
    this.imageCreationEnabled = !this.imageCreationEnabled;
  }

  get tooltipEnableSearch(): string {
    // 1) If the currently-selected LLM supports any grounding at all, show that LLM’s displayedName.
    if (this.isSearchSupported) {
      const entry = Object.values(this.llmConfigService.llmConfigs).find(
        (c) => c.technicalName === this.modelTechnicalName,
      );
      const nameToShow = entry ? entry.displayedName : this.modelTechnicalName;
      return $localize`:@@chat_input_search_toggle_tooltip_enable:Activer la recherche avec le modèle ${nameToShow}`;
    }

    // 2) Otherwise (the selected LLM cannot ground), fall back to the “default search LLM.”
    const defaultSearch = this.configService.searchLlm;
    const defaultEntry = Object.values(this.llmConfigService.llmConfigs).find((c) => c.technicalName === defaultSearch);
    const defaultName = defaultEntry ? defaultEntry.displayedName : defaultSearch;
    return $localize`:@@chat_input_search_toggle_tooltip_enable:Activer la recherche avec le modèle ${defaultName}`;
  }

  get tooltipDisableSearch(): string {
    // 1) If the currently-selected LLM supports grounding, show that LLM’s displayedName.
    if (this.isSearchSupported) {
      const entry = Object.values(this.llmConfigService.llmConfigs).find(
        (c) => c.technicalName === this.modelTechnicalName,
      );
      const nameToShow = entry ? entry.displayedName : this.modelTechnicalName;
      return $localize`:@@chat_input_search_toggle_tooltip_disable:Désactiver la recherche avec le modèle ${nameToShow}`;
    }

    // 2) Otherwise fall back to default search LLM.
    const defaultSearch = this.configService.searchLlm;
    const defaultEntry = Object.values(this.llmConfigService.llmConfigs).find((c) => c.technicalName === defaultSearch);
    const defaultName = defaultEntry ? defaultEntry.displayedName : defaultSearch;
    return $localize`:@@chat_input_search_toggle_tooltip_disable:Désactiver la recherche avec le modèle ${defaultName}`;
  }

  get tooltipEnableImageCreation(): string {
    const tech = this.configService.imageLlm;
    const entry = Object.values(this.llmConfigService.llmConfigs).find((c) => c.technicalName === tech);
    const name = entry?.displayedName ?? tech;
    return $localize`:@@chat_input_image_toggle_tooltip_enable:Activer la création/édition d'images avec ${name}`;
  }

  get tooltipDisableImageCreation(): string {
    const tech = this.configService.imageLlm;
    const entry = Object.values(this.llmConfigService.llmConfigs).find((c) => c.technicalName === tech);
    const name = entry?.displayedName ?? tech;
    return $localize`:@@chat_input_image_toggle_tooltip_disable:Désactiver la création/édition d'images avec ${name}`;
  }

  get isSearchSupported(): boolean {
    const config = Object.values(this.llmConfigService.llmConfigs).find(
      (c) => c.technicalName === this.modelTechnicalName,
    );
    return config?.searchSupported === true;
  }

  get isImageCreationSupported(): boolean {
    const cfg = Object.values(this.llmConfigService.llmConfigs).find(
      (c) => c.technicalName === this.modelTechnicalName,
    );
    return cfg?.imageCreationSupported === true;
  }

  sendOrStop(): void {
    if (this.genPending) {
      this.stopClick.emit();
    } else {
      this.textAreaInput = this.textAreaInput.trim();
      if (!this.textAreaInput) {
        return;
      }
      this.sendClick.emit({
        prompt: this.textAreaInput,
        files: this.inputFiles,
        content: this.inputContent,
        enableSearch: this.searchEnabled,
        enableImageCreation: this.imageCreationEnabled,
      });
      this.textAreaInput = "";
      this.inputFiles = [];
    }
  }

  onFileSelected(files: FileInfo[]): void {
    const selectedFileIds = new Set(files.map((file) => file.id));
    this.inputFiles = this.inputFiles.filter((file) => !selectedFileIds.has(file.id));
    this.inputFiles = this.inputFiles.concat(files);
  }

  onContentSelected(content: DragAndDropItem): void {
    this.inputContent = content;
  }

  removeSelectedFile(id: string): void {
    this.inputFiles = this.inputFiles.filter((file) => file.id !== id);
  }

  removeSelectedContent(): void {
    this.inputContent = undefined;
  }
}
